import { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Row, Spinner, Tab, Tabs } from 'react-bootstrap';
import { ABAS_EMPRESA, abasParaAdmin, paraApi, valoresIniciais } from './camposEmpresa';
import EnderecoEmpresa from './EnderecoEmpresa';
import LogoEmpresa from './LogoEmpresa';
import { useSenhaPadrao } from '../../lib/senhaPadrao';
import { buscarEnderecoPorCep } from '../../lib/cep';
import { useToast } from '../../context/ToastContext';
import { api } from '../../lib/api';

export default function FormEmpresa({ ehNova, restrito, empresaInicial, aoSalvar }) {
  // restrito = admin da imobiliária (nivel 2) editando a PRÓPRIA empresa: só as abas/campos permitidos
  const abas = restrito ? abasParaAdmin() : ABAS_EMPRESA;
  const toast = useToast();
  const { senhaPadrao } = useSenhaPadrao();
  const [valores, setValores] = useState(() => valoresIniciais(empresaInicial));
  const [salvando, setSalvando] = useState(false);
  const [buscandoCep, setBuscandoCep] = useState(null);
  // Recursos liberados pro plano da empresa (ex.: envio para grupos do WhatsApp) — só o superadmin liga.
  // O admin da imobiliária só consegue liberar pros corretores o que estiver ligado aqui.
  const [catalogoRecursos, setCatalogoRecursos] = useState([]);
  const [recursos, setRecursos] = useState(() => empresaInicial?.recursos || {});

  useEffect(() => {
    if (restrito) return;
    api
      .get('/permissoes')
      .then((r) => setCatalogoRecursos(r.data.recursos.filter((p) => p.porEmpresa)))
      .catch(() => {});
  }, [restrito]);

  function atualizarCampo(nome, valor) {
    setValores((v) => ({ ...v, [nome]: valor }));
  }

  // ViaCEP ao sair do campo (ver FormCliente.js pro mesmo padrão/motivo).
  async function aoSairDoCep(campo) {
    if (!campo.preencheEndereco) return;
    setBuscandoCep(campo.nome);
    const endereco = await buscarEnderecoPorCep(valores[campo.nome]);
    setBuscandoCep(null);
    if (!endereco) return;
    const mapa = campo.preencheEndereco;
    setValores((v) => ({
      ...v,
      [mapa.endereco]: endereco.endereco || v[mapa.endereco],
      [mapa.bairro]: endereco.bairro || v[mapa.bairro],
      [mapa.cidade]: endereco.cidade || v[mapa.cidade],
      [mapa.estado]: endereco.estado || v[mapa.estado],
    }));
  }

  async function aoEnviar(e) {
    e.preventDefault();
    setSalvando(true);
    try {
      const dados = paraApi(valores);
      if (!restrito) dados.recursos = recursos;
      await aoSalvar(dados);
    } catch (err) {
      toast.erro(err.message || 'Não foi possível salvar');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Form onSubmit={aoEnviar}>
      {ehNova && (
        <Alert variant="info">
          Ao salvar, um usuário admin (nível 2) é criado automaticamente pra essa empresa, usando o e-mail
          informado abaixo — senha inicial padrão <strong>{senhaPadrao}</strong>, com troca obrigatória no primeiro
          acesso.
        </Alert>
      )}

      <Tabs defaultActiveKey={0} className="mb-3">
        {abas.map((aba, i) => (
          <Tab key={aba.titulo} eventKey={i} title={aba.titulo}>
            {/* Endereço: mesma tela do cadastro de imóveis (CEP, número, complemento e mapa com pino) */}
            {aba.titulo === 'Endereço' ? (
              <EnderecoEmpresa valores={valores} setCampo={atualizarCampo} />
            ) : (
            <Row className="g-3 pt-2">
              {aba.campos.map((campo) => (
                <Col md={campo.tipo === 'checkbox' || campo.tipo === 'logo' ? 12 : 6} key={campo.nome}>
                  {campo.tipo === 'logo' ? (
                    <LogoEmpresa publicId={empresaInicial?.public_id} imagem={valores.e1_imagem} url={empresaInicial?.e1_imagem_url} setCampo={atualizarCampo} />
                  ) : campo.tipo === 'checkbox' ? (
                    <Form.Check
                      type="checkbox"
                      id={campo.nome}
                      label={campo.rotulo}
                      checked={valores[campo.nome]}
                      onChange={(e) => atualizarCampo(campo.nome, e.target.checked)}
                    />
                  ) : (
                    <Form.Group controlId={campo.nome}>
                      <Form.Label>
                        {campo.rotulo}
                        {buscandoCep === campo.nome && (
                          <Spinner animation="border" size="sm" className="ms-2" />
                        )}
                      </Form.Label>
                      <Form.Control
                        type={campo.tipo}
                        value={valores[campo.nome]}
                        required={campo.obrigatorio}
                        onChange={(e) => atualizarCampo(campo.nome, e.target.value)}
                        onBlur={campo.preencheEndereco ? () => aoSairDoCep(campo) : undefined}
                      />
                    </Form.Group>
                  )}
                </Col>
              ))}
            </Row>
            )}
          </Tab>
        ))}
      </Tabs>

      {!restrito && catalogoRecursos.length > 0 && (
        <div className="border rounded p-3 mb-3 bg-light">
          <div className="fw-semibold mb-1">Recursos liberados para esta empresa (plano)</div>
          <div className="text-muted small mb-2">
            O administrador da imobiliária só consegue liberar para os corretores os recursos ligados aqui. Cada usuário
            ainda precisa da permissão dele (tela de Usuários).
          </div>
          {catalogoRecursos.map((p) => (
            <Form.Check
              key={p.chave}
              type="switch"
              id={`recurso-${p.chave}`}
              label={p.rotulo}
              checked={recursos[p.chave] === true}
              onChange={(e) => setRecursos((atual) => ({ ...atual, [p.chave]: e.target.checked }))}
            />
          ))}
        </div>
      )}

      <Button type="submit" variant="primary" disabled={salvando}>
        {salvando ? <Spinner animation="border" size="sm" /> : 'Salvar'}
      </Button>
    </Form>
  );
}
